import { partition } from 'lodash'
import { Action, ActionType, Field, State } from './types'
import { fuzzySearch, getDefaultSelected, getFilteredFieldIds, getAllSelected } from './utils'

export function reducer(state: State, action: Action): State {
  switch (action.type) {
    case ActionType.clearSearch: {
      const { searchTerm, selected } = action.payload

      return {
        ...state,
        searchTerm,
        selected,
      }
    }
    case ActionType.resetToDefault: {
      const { allColumns } = state
      const visibleFields = action.payload
      const defaultSelected = getDefaultSelected(
        allColumns,
        visibleFields.map(field => field?.rowPropertyName || String(field?.fieldId)),
      )

      return {
        ...state,
        fieldsExpanded: true,
        propertiesExpanded: true,
        searchResults: [],
        searchTerm: '',
        selected: defaultSelected,
      }
    }
    case ActionType.resetToInitialState: {
      return {
        ...state,
        popoverOpen: false,
        searchResults: [],
        searchTerm: '',
      }
    }
    case ActionType.setFieldsExpanded: {
      return {
        ...state,
        fieldsExpanded: !state.fieldsExpanded,
      }
    }
    case ActionType.setCreativeClassificationsExpanded: {
      return {
        ...state,
        creativeClassificationsExpanded: !state.creativeClassificationsExpanded,
      }
    }
    case ActionType.setPropertiesExpanded: {
      return {
        ...state,
        propertiesExpanded: !state.propertiesExpanded,
      }
    }
    case ActionType.setPopoverOpen: {
      return {
        ...state,
        popoverOpen: action.payload,
      }
    }
    case ActionType.setSearchResults: {
      const { searchTerm } = action.payload
      const { allColumns } = state

      const results = fuzzySearch(allColumns, searchTerm)

      return {
        ...state,
        searchResults: results,
        searchTerm,
      }
    }
    case ActionType.setSelected: {
      const { rowPropertyName, fieldId, checked } = action.payload
      const { selected, searchResults, allColumns, fieldsExpanded, propertiesExpanded } = state

      const [properties, fields] = partition(allColumns, 'rowPropertyName')

      const filteredFieldIds = getFilteredFieldIds(
        fields,
        searchResults,
        fieldsExpanded,
        propertiesExpanded,
      )

      const filteredRowPropertyNames = getFilteredFieldIds(
        properties,
        searchResults,
        fieldsExpanded,
        propertiesExpanded,
      )

      const newSelected = {
        ...selected,
        [rowPropertyName || fieldId]: checked,
      }

      return {
        ...state,
        selected: newSelected,
        selectAllFields: getAllSelected(filteredFieldIds, newSelected),
        selectAllProperties: getAllSelected(filteredRowPropertyNames, newSelected),
      }
    }
    case ActionType.selectFields: {
      const { selectAllFields, selected } = state

      const fieldsToSelect = getFieldsToSelect(action.payload, [])

      const newSelected = fieldsToSelect.reduce(
        (acc, field) => ({
          ...acc,
          [field.fieldId]: !selectAllFields,
        }),
        { ...selected },
      )

      return {
        ...state,
        selected: newSelected,
        selectAllFields: !selectAllFields,
      }
    }

    case ActionType.selectCreativeClassifications: {
      const { selectAllFields, selected } = state

      const fieldsToSelect = getFieldsToSelect(action.payload, [])

      const newSelected = fieldsToSelect.reduce(
        (acc, field) => ({
          ...acc,
          [field.fieldId]: !selectAllFields,
        }),
        { ...selected },
      )

      return {
        ...state,
        selected: newSelected,
        selectAllFields: !selectAllFields,
      }
    }

    case ActionType.selectProperties: {
      const { selectAllProperties, selected } = state

      const fieldsToSelect = getFieldsToSelect([], action.payload)

      const newSelected = fieldsToSelect.reduce(
        (acc, field) => ({
          ...acc,
          [field.rowPropertyName as string]: !selectAllProperties,
        }),
        { ...selected },
      )

      return {
        ...state,
        selected: newSelected,
        selectAllProperties: !selectAllProperties,
      }
    }
    case ActionType.updateInitialValues: {
      const { allColumns, rowProperties, selected } = action.payload

      return {
        ...state,
        allColumns,
        rowProperties,
        selected,
      }
    }
    default:
      return state
  }

  function getFieldsToSelect(fields: Field[], properties: Field[]): Field[] {
    const { searchResults } = state

    if (searchResults.length > 0) {
      return searchResults
    }

    if (properties.length > 0) {
      return properties
    }

    return fields
  }
}
